import { memo, useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BodyComponent,
  trans,
  HeaderComponent,
  PaginationComponent,
  CreateButton, RefreshButton, DownloadButton, ImportButton,
  DeleteButton,
} from "@comps/components";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { makeRequest, makeRequestStateless, handleDownloadFile } from "@utils/helpers";
import { Const } from "../constants";
import { Button, message, Popconfirm, Popover, Spin, Tooltip } from 'antd';
import { FilterComponent, FilterButton } from "@comps/components";
import { deactivateRosterAssignmentReq, downloadSampleReq, getAllDataReq, getFilterReq, getTotalHoursReq } from "./requests";
import CreateCompanyRoaster from "./components/CreateCompanyRoaster";
import moment from "moment";
import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, WarningOutlined } from "@ant-design/icons";
import DeleteCompanyRoaster from "./components/DeleteCompanyRoaster";
import { deleteMultipleShiftsReq, deleteShiftReq } from "../myRoster/requests";
import CreateShift from "../myRoster/components/CreateShift";
import UpdateShift from "../myRoster/components/UpdateShift";
import "../common/Roster.css"
import { convertMinutesToHours, convertTimeTotz, checkPermissions } from '@comps/commonFunctions';
import { getDatesFilters, getOrganizationFilters, getSubordinateScopeFilters } from "../../common/commonFilterFields";
import ImportSample from "../../common/ImportSample";
import { pusherInitializer, decryptLocalData } from "@comps/commonFunctions";
import { useLocation } from "react-router-dom";

const langKey = Const["companyRoaster"].lngKey;
const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const["companyRoaster"].route
      }
    ]
  }
}

const AVATAR_PALETTE = ["#C25A14", "#3F7D58", "#6C5CE7", "#2B7FE0", "#B5462E", "#C99A2E", "#2E9BA6", "#B0479B"];

const rosterInitials = (name = "") => {
  const parts = name
    .replace(/\(.*?\)/g, " ")
    .replace(/[-–—].*$/, " ")
    .replace(/[^A-Za-z\s]/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const rosterAvatarColor = (name = "") => {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
};

const isWeekendDate = (date) => [5, 6].includes(moment(date).day());

// Small round avatar: profile photo when it loads, else colored initials tile.
const RosterAvatar = memo(({ employee }) => {
  const [err, setErr] = useState(false);
  const url = employee.profile_pic_url;
  const showPic = url && !url.includes("arnold-avatar") && !err;
  return showPic
    ? <img className="rz-ea" src={ url } onError={ () => setErr(true) } alt="" />
    : <div className="rz-ea" style={ { background: rosterAvatarColor(employee.full_name || "") } }>{ rosterInitials(employee.full_name || "") }</div>;
});

const ShiftTiming = memo(({ startTime, endTime, startPeriod, endPeriod }) => (
  <>
    <div className="rz-cell-time">{ startTime } – { endTime }</div>
    <div className="rz-cell-icons">
      <span>{ startPeriod }</span>
      <span>{ endPeriod }</span>
    </div>
  </>
));

const RosterCell = memo(({
  cell,
  multipleEdit,
  dateTotal,
  onEditSingleShift,
  renderActions,
}) => {
  const { date, employeeId, shift, isSelected, startTime, endTime, startPeriod, endPeriod, baseColor } = cell;
  const isDayOff = dateTotal === 0 || !shift;

  const cellInner = isDayOff
    ? <div className="rz-cell rz-off">Day Off</div>
    : (
      <div
        className={ `rz-cell${isSelected ? " rz-selected" : ""}` }
        style={ { background: baseColor } }
        onClick={ multipleEdit ? () => onEditSingleShift(shift) : undefined }
      >
        { multipleEdit &&
          <div className="rz-cell-mark">{ isSelected ? <CheckOutlined /> : <CloseOutlined /> }</div>
        }
        <ShiftTiming startTime={ startTime } endTime={ endTime } startPeriod={ startPeriod } endPeriod={ endPeriod } />
      </div>
    );

  return (
    <td className="rz-shiftcell">
      { multipleEdit ? cellInner : (
        <Popover placement="bottom" title={ '' } content={ renderActions(date, employeeId, shift) } trigger="click">
          { cellInner }
        </Popover>
      ) }
    </td>
  );
});

const RosterTable = memo(({
  dateColumns,
  rosterGrid,
  dateTotalSource,
  multipleEdit,
  onEditSingleShift,
  renderActions,
}) => (
  <div className="rz-scroll">
    <table className="rz-table">
      <thead>
        <tr>
          <th className="rz-emph rz-sticky">Employee</th>
          { dateColumns.map((date, mins) => (
            <th key={ mins } className="rz-dateh">
              <span className={ isWeekendDate(date) ? "rz-weekend" : "rz-weekday" }>
                <span className="rz-dow">{ moment(date).format('ddd') }</span>
                <span>{ moment(date).format('DD') }</span>
              </span>
            </th>
          )) }
        </tr>
      </thead>
      <tbody>
        { rosterGrid.map(({ employee, totalHours, cells }) => (
          <tr key={ employee.id }>
            <td className="rz-empcell rz-sticky">
              <div className="rz-emp">
                <RosterAvatar employee={ employee } />
                <div className="rz-emp-meta">
                  <div className="rz-name">{ employee.full_name }</div>
                  <div className="rz-code">{ employee.code }</div>
                </div>
                { totalHours && <div className="rz-hours">{ totalHours }</div> }
              </div>
            </td>
            { cells.map((cell, mins) => (
              <RosterCell
                key={ `${cell.date}-${employee.id}-${mins}` }
                cell={ cell }
                multipleEdit={ multipleEdit }
                dateTotal={ dateTotalSource[cell.date] }
                onEditSingleShift={ onEditSingleShift }
                renderActions={ renderActions }
              />
            )) }
          </tr>
        )) }
        <tr className="rz-totalrow">
          <td className="rz-total-lbl rz-sticky">Total</td>
          { dateColumns.map((value, mins) => (
            <td key={ value + mins }>
              <span className="rz-total-val">{ dateTotalSource[value] ? convertMinutesToHours(dateTotalSource[value]) : '-' }</span>
            </td>
          )) }
        </tr>
      </tbody>
    </table>
  </div>
));

const IndexCompanyRoaster = (props) => {
  const { onDataChange } = props;
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);

  const [filters, setFilters] = useState('');
  const [employments, setEmployments] = useState([]);
  const [dateTotalSource, setDateTotalSource] = useState([]);
  const [empTotal, setempTotal] = useState({});
  const [shifts, setShifts] = useState([])
  const [fromDate, setFromDate] = useState()
  const [toDate, setToDate] = useState()
  const [editModal, setEditModal] = useState(null);
  const [loading, setloading] = useState(true)
  const [multipleEdit, setMultipleEdit] = useState(false)
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 5,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });
  const selectedShiftIdsRef = useRef([]);

  const cleanFilterValue = useCallback((value = '') => {
    if (!value) {
      return '';
    }
    return value.replace('employees_|undefined;', '');
  }, []);

  const mergedFilters = useMemo(() => {
    const baseFilter = cleanFilterValue(props.filters);
    const localFilter = cleanFilterValue(filters);
    return `${baseFilter};${localFilter}`;
  }, [cleanFilterValue, props.filters, filters]);

  const dateColumns = useMemo(() => Object.keys(dateTotalSource), [dateTotalSource]);
  const selectedShiftIdSet = useMemo(() => new Set(selectedShiftIds), [selectedShiftIds]);
  const filterFields = useMemo(() => {
    const nextFilterArray = getOrganizationFilters('');

    nextFilterArray.push({
      type: "select",
      placeholder: trans(`${langKey}.time_slot_id`),
      key: "time_slot",
      data_key: "supporting_data.time_slot",
    });

    if (props.module !== 'myRoaster') {
      nextFilterArray.unshift({
        type: "select",
        placeholder: 'Employee',
        key: "employees",
        data_key: "supporting_data.employments",
        resource_type: 'employment'
      });
    }

    return nextFilterArray.concat(getDatesFilters(''));
  }, [props.module]);

  const rosterGrid = useMemo(() => {
    return employments.map((employee) => {
      const totalHours = empTotal && empTotal[employee.id] !== undefined
        ? convertMinutesToHours(parseFloat(empTotal[employee.id]))
        : '';

      const cells = dateColumns.map((date) => {
        const dateKey = `${moment(date).format('YYYYMMDD')}-${employee.id}`;
        const shift = shifts?.[dateKey]?.[0] || null;
        const isSelected = shift ? selectedShiftIdSet.has(shift.id) : false;

        if (!shift) {
          return {
            date,
            employeeId: employee.id,
            shift: null,
            isSelected: false,
            startTime: '',
            endTime: '',
            startPeriod: '',
            endPeriod: '',
            baseColor: 'rgba(234, 67, 53, 0.10)',
          };
        }

        const start = convertTimeTotz(shift.start);
        const end = convertTimeTotz(shift.end);

        return {
          date,
          employeeId: employee.id,
          shift,
          isSelected,
          startTime: start.format('hh:mm'),
          endTime: end.format('hh:mm'),
          startPeriod: start.format('A'),
          endPeriod: end.format('A'),
          baseColor: shift.bg_colour,
        };
      });

      return {
        employee,
        totalHours,
        cells,
      };
    });
  }, [dateColumns, employments, empTotal, selectedShiftIdSet, shifts]);


  // ... (other code)
  // ==================================
  //   Use States End
  // ==================================

  // ================================== 
  //   Table Data End
  // ==================================
  //==========================Start Pusher=================================
  //==========================end Pusher =================================

  useEffect(() => {
    setFilters('')
    // eslint-disable-next-line
  }, [props]);

  // ==================================
  //   Table Data End
  // ==================================
  const getEmployments = useCallback((id) => {
    void id;
  }, []);

  const onSuccess = useCallback((response) => {

    setPagination(response.pagination);
    setChildComponent(null);
    setEditModal(null)
    setEmployments(response.employments);
    setFromDate(response.from_date)
    setToDate(response.to_date)

    //   Object.values(response.shifts).forEach(each => {
    //     each[0]['selected']=false;
    // });

    setShifts(response.shifts)

    // Optional callback so embedders (e.g. the onboarding wizard) can react to
    // the currently loaded roster. No-op for the standalone route.
    onDataChange?.(response.shifts, response.employments)

    let empids = ''
    response.employments.forEach((each) => {
      empids += `${each.id},`
    })
    const payload = {
      page: currentPage,
      filters: mergedFilters,
      empIds: empids
    }
    makeRequest(setLoader, getTotalHoursReq, payload, onGetTotalHours, null);
  }, [currentPage, mergedFilters, onDataChange]);

  const getAllData = useCallback(() => {
    setloading(true)
    selectedShiftIdsRef.current = [];
    setSelectedShiftIds([]);
    const payload = {
      page: currentPage,
      filters: mergedFilters
    }
    makeRequest(setLoader, getAllDataReq, payload, onSuccess, onErrorCreatingRoaster);
  }, [currentPage, mergedFilters, onSuccess]);

  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user || !user.tenant) {
      return;
    }

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    const refreshRoster = () => {
      // getAllData();
      // getEmployments();
    };

    channel.bind('shift-creation-errors-event', refreshRoster);
    channel.bind('shifts-created-event', refreshRoster);

    return () => {
      channel.unbind('shift-creation-errors-event', refreshRoster);
      channel.unbind('shifts-created-event', refreshRoster);
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [getAllData, getEmployments]);

  useEffect(() => {
    getAllData();
    getEmployments();
    setMultipleEdit(false);
  }, [location.pathname, getAllData, getEmployments]);
  const onErrorCreatingRoaster = (err, res) => {
  }
  const onGetTotalHours = (response) => {
    setDateTotalSource(response.date_total)
    setempTotal(response.emp_total)
    // setLoader(false)
    setloading(false)
  }

  const cancel = (e) => {

  };
  const onDeleted = useCallback((res) => {
    message.success("Record Deleted")
    getAllData();
  }, [getAllData]);
  const onError = useCallback((err, res) => {
    message.info(res.data.message);
    getAllData();
    //setErrors(errorList);
  }, [getAllData])
  const onDeleteShift = useCallback((shift) => {
    makeRequest(setLoader, deleteShiftReq, shift.id, onDeleted, onError);
  }, [onDeleted, onError]);
  const onDeactivateRosterAssignment = useCallback((shift) => {
    makeRequest(setLoader, deactivateRosterAssignmentReq, { id: shift.roster_assignment_id }, () => {
      message.success("Auto apply turned off for this roster")
      getAllData();
    }, onError);
  }, [getAllData, onError]);

  // Create component modal 
  const onDeleteAll = () => {
    setChildComponent(<DeleteCompanyRoaster onCreated={ onCreated } employments={ employments } page={ currentPage } />);
  };
  const onCreate = () => {
    setChildComponent(<CreateCompanyRoaster onCreated={ onCreated } onSuccess={ onSuccess } page={ currentPage } />);
  };

  const onCreated = useCallback((data) => {
    if (data) {
      setEmployments(data.employments);
      setShifts(data.shifts)
      getAllData()
    }

    setChildComponent(null);
    setEditModal(null)
  }, [getAllData]);
  const onCreateSingleShift = useCallback((date, employment_id) => {
    setEditModal(
      <CreateShift onCreated={ onCreated } date={ date } employment_id={ employment_id } employments={ employments } getAllData={ getAllData } filters={ `${props.filters};${filters}` } page={ currentPage } />
    );
  }, [currentPage, employments, filters, getAllData, onCreated, props.filters]);

  const onEnableDisableMultiple = () => {
    setMultipleEdit(!multipleEdit);
    selectedShiftIdsRef.current = [];
    setSelectedShiftIds([]);
  }
  const onEditSingleShift = useCallback((shift) => {
    if (multipleEdit) {
      const exists = selectedShiftIdsRef.current.includes(shift.id);
      const nextSelectedShiftIds = exists
        ? selectedShiftIdsRef.current.filter((id) => id !== shift.id)
        : [...selectedShiftIdsRef.current, shift.id];

      selectedShiftIdsRef.current = nextSelectedShiftIds;
      setSelectedShiftIds(nextSelectedShiftIds);
    }
    else {
      setEditModal(
        <UpdateShift onCreated={ onCreated } shift={ shift } getAllData={ getAllData } multiple={ false } filters={ `${props.filters};${filters}` } page={ currentPage } />
      );
    }
  }, [currentPage, filters, getAllData, multipleEdit, onCreated, props.filters]);

  const onEditMultipleShift = () => {
    setEditModal(
      <UpdateShift onCreated={ onCreated } shift={ null } getAllData={ getAllData } multiple={ true } edit={ true } shiftIds={ selectedShiftIds } filters={ `${props.filters};${filters}` } page={ currentPage } />
    );
  }
  const onDeleteMultipleShifts = () => {
    let payload = {
      shift: selectedShiftIds
    }
    makeRequest(setLoader, deleteMultipleShiftsReq, payload, onDeleted, onError);
  }


  const onViewSingleShift = useCallback((shift) => {
    setEditModal(
      <UpdateShift onCreated={ onCreated } shift={ shift } getAllData={ getAllData } disabled={ true } filters={ `${props.filters};${filters}` } page={ currentPage } />
    );
  }, [currentPage, filters, getAllData, onCreated, props.filters]);

  //download sample log
  const onDownloadSample = () => {
    makeRequestStateless(downloadSampleReq, null, onSuccessDownloadSample, onSuccessDownloadSample);
  }
  const onSuccessDownloadSample = (res, err) => {
    if (res.code === undefined) {
      // Extract file extension
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf'
      const fileName = `roster_sample_import.${fileExtension}`
      handleDownloadFile(fileName, res)
    }
    else {
      message.error(res.message)
    }
  }

  const renderActions = useCallback((date, employment_id, shift) => {
    return (
      <div>
        { shift ?
          <>
            <div style={ { display: 'flex', flexDirection: 'column' } }>

              <Button style={ { border: 'none' } } icon={ <EditOutlined style={ { color: '#14AE5C' } } /> } onClick={ () => onEditSingleShift(shift) } disabled={ !checkPermissions(null, 'update') }>Edit</Button>
              <Button style={ { border: 'none' } } icon={ <EyeOutlined style={ { color: '#4079FC' } } /> } onClick={ () => onViewSingleShift(shift) } disabled={ !checkPermissions(null, 'view') }>View</Button>
              { shift.roster_assignment_id ?
                <Popconfirm style={ { width: 590, height: 140 } }
                  icon={ <WarningOutlined style={ { color: 'red' } } /> }
                  title="Turn off auto apply"
                  description="This will stop future weekly roster auto-allotment and remove safe future shifts from this assignment. Protected shifts will remain."
                  onConfirm={ () => onDeactivateRosterAssignment(shift) }
                  onCancel={ cancel }
                  cancelText={ <div style={ { border: 'none', background: "none", color: "#4079DF", width: 71, height: 58 } }>Cancel</div> }
                >
                  <Button style={ { border: 'none' } } icon={ <StopOutlined style={ { color: '#EA4335' } } /> } disabled={ !checkPermissions(null, 'update') }>Turn off auto apply</Button>
                </Popconfirm>
                : null }
              <Popconfirm style={ { width: 590, height: 140 } }
                icon={ <WarningOutlined style={ { color: 'red' } } /> }
                title="Delete"
                description="Are you sure you want to delete this.?"
                onConfirm={ () => onDeleteShift(shift) }
                onCancel={ cancel }
                Text={ <Button style={ { border: 'none', background: "none", color: "white", width: 71, height: 58 } }>Delete</Button> }
                cancelText={ <div style={ { border: 'none', background: "none", color: "#4079DF", width: 71, height: 58 } }>Cancel</div> }
              >
                <Button style={ { border: 'none' } } icon={ <DeleteOutlined style={ { color: '#EA4335' } } /> } disabled={ !checkPermissions(null, 'delete') }>Delete</Button>
              </Popconfirm>
              {/* <Button style={ { border: 'none' } } icon={ <CloseOutlined /> }>Close</Button> */ }
            </div>
          </>
          :
          <div style={ { display: 'flex', flexDirection: 'row' } }>
            <Button style={ { border: 'none' } } icon={ <PlusOutlined className="text-primary" /> } onClick={ () => onCreateSingleShift(date, employment_id) } disabled={ !checkPermissions(null, 'create') }>Shift</Button>
          </div>
        }
      </div>
    );
  }, [onCreateSingleShift, onDeactivateRosterAssignment, onDeleteShift, onEditSingleShift, onViewSingleShift]);
  //---------------filters-----------------------
  const onOpenFilter = () => {
    setChildComponent(<FilterComponent filters={ filterFields } modkey={ props.modkey } selected={ filters } api={ getFilterReq } optionScopeFilters={ getSubordinateScopeFilters(props.filters) } setCurrentPage={ setCurrentPage } onFilter={ setFilters } onCloseFilter={ onCloseFilter }
    />
    )
  }

  const onCloseFilter = () => {
    setChildComponent(null);
  }
  //----end filter--------------------
  const onClickBackDate = (start, end) => {
    message.info("Loading shifts ...")
    setloading(true)
    const from = moment(start).subtract(1, 'months').format('YYYY-MM-DD');
    const to = moment(end).subtract(1, 'months').format('YYYY-MM-DD');
    const payload = `${filters};from_|${from};to_|${to};`
    setLoader(true)
    setFilters(payload)
  }

  const onClickNextDate = (start, end) => {
    message.info("Loading shifts ...")
    setloading(true)
    const from = moment(start).add(1, 'months').format('YYYY-MM-DD');
    const to = moment(end).add(1, 'months').format('YYYY-MM-DD');
    const payload = `${filters};from_|${from};to_|${to};`
    setLoader(true)
    setFilters(payload)
  }

  const onRefresh = () => {
    getAllData()
    setloading(true)
    getEmployments()
  }
  return (
    <>

      { childComponent }
      <BodyComponent>

        <HeaderComponent className="header-component" headers={ pageConfig.headers }>
          { props.module === 'myRoaster' && <RefreshButton onClick={ onRefresh } /> }
          {

            props.module !== 'myRoaster' && <>
              <RefreshButton onClick={ onRefresh } />
              <DownloadButton onClick={ onDownloadSample } text="Download roster template" />
              <ImportButton
                modkey={ props.modkey }
                onClick={ () => setChildComponent(<ImportSample onSuccess={ getAllData } api="roster/import" module="Roster" onCompleted={ () => setChildComponent(null) } />) }
                text="Upload roster import"
              />
              { checkPermissions(props.modkey, 'create') && <CreateButton onClick={ onEnableDisableMultiple } text={ multipleEdit ? "Disable Multiselect" : "Enable Multiselect" } className={ multipleEdit ? 'bg-danger' : "bg-success" } /> }
              <DeleteButton onClick={ onDeleteAll } disabled={ !checkPermissions(null, 'delete') } text="Delete Roster" />
              <CreateButton text="Add Roster" onClick={ onCreate } disabled={ !checkPermissions(null, 'create') } />
            </>
          }

          {
            props.module !== 'myRoaster' && <FilterButton onClick={ onOpenFilter } />
          }
          {/* <MultiselectButton className="multiselect-button" />
          <ImportButton className="import-button" style={ { position: 'absolute', top: 60, right: 172 } } /> */}


        </HeaderComponent>


        <div style={ {
          display: 'flex',
          justifyContent: 'right',
          marginTop: 8,
          marginBottom: 8
        } }>

          { multipleEdit && selectedShiftIds.length > 0 &&
            <>
              <Popconfirm style={ { width: 590, height: 140 } }
                icon={ <WarningOutlined style={ { color: 'red' } } /> }
                title="Delete"
                description="Are you sure to delete selected shifts?"
                onConfirm={ () => onDeleteMultipleShifts() }
                onCancel={ cancel }
                okText="Yes"
                cancelText="No"
              >
                <DeleteButton disabled={ !checkPermissions(null, 'delete') } style={ {
                  marginRight: 10
                } } text={ "Delete Multiple" } />
              </Popconfirm>

              <CreateButton disabled={ !checkPermissions(null, 'create') } style={ {
                marginRight: 10
              } } onClick={ () => onEditMultipleShift() } text={ "Edit Multiple" } className={ "bg-info" } />
            </>
          }


        </div>


        <div style={ {
          marginTop: 0
        } }>
          { editModal }
          <div className="rz-panel">
            <div className="rz-panel-b">
              <div className="rz-monthnav">
                <Tooltip placement="top" title="Previous Month">
                  <button className="rz-navbtn" onClick={ () => onClickBackDate(fromDate, toDate) }><LeftOutlined /></button>
                </Tooltip>
                <span className="rz-range">
                  { moment(fromDate).format('DD MMMM YYYY') } { " — " } { moment(toDate).format('DD MMMM YYYY') }
                </span>
                <Tooltip placement="top" title="Next Month">
                  <button className="rz-navbtn" onClick={ () => onClickNextDate(fromDate, toDate) }><RightOutlined /></button>
                </Tooltip>
              </div>
              <Spin spinning={ loading || loader } tip="Loading">
                <RosterTable
                  dateColumns={ dateColumns }
                  rosterGrid={ rosterGrid }
                  dateTotalSource={ dateTotalSource }
                  multipleEdit={ multipleEdit }
                  onEditSingleShift={ onEditSingleShift }
                  renderActions={ renderActions }
                />
              </Spin>
            </div>
          </div>
        </div >



      </BodyComponent >
      <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } />
    </>
  );
};
export default IndexCompanyRoaster;
